import * as React from 'react';
import * as classnames from 'classnames';
import Select from 'react-select';
import SettingBoxLayout from '../settingsBox/SettingBox';
import Toggle from '../../../../common/Toggle';
import ShowSetting from './showSetting';
import IndicatorsContainer from '../../../../common/reactSelect/IndicatorsContainer';
import * as optionSettings from '../../../../../mapping/optionSettings';
import HelperTooltip from '../../../../common/HelperTooltip';

interface IQuestionSettingsProps {
    question: any;
    questionSettings: {
        allowUserEdit?: boolean;
        showContinueButton?: boolean;
        showNotApplicableOption?: boolean;
        alernateQuestionWording?: boolean;
        topics?: boolean;
        randomGroup?: boolean;
        piping?: boolean;
        showGenericProbe?: boolean;
        showQualityControl?: boolean;
        gibberish?: boolean;
        lazyAnswer?: boolean;
        showValidation?: boolean;
        allowCopyPaste?: boolean;
        boxVisibility?: boolean;
        popupCardType?: boolean;
        showFollowUp?: boolean;
        showMin?: boolean;
        showMax?: boolean;
        useThousandSeparator?: boolean;
        allowDecimal?: boolean;
        showDisappearingMedia?: boolean;
        tradeoffTypes?: boolean;
        systemTextsOverride?: boolean;
        skipPractice?: boolean;
        allowSkipFirstStage?: boolean;
    };
    disableQuestionSettings?: {
        allowUserEdit?: boolean;
    };
    errorObject?: any;
    disabled: boolean;
    onClose: () => void;
    setValue: (keyName: string, value: any) => void;
}

const QuestionSettings: React.SFC<IQuestionSettingsProps> = ({
    onClose,
    questionSettings,
    disableQuestionSettings,
    question,
    setValue,
    disabled,
    errorObject
}) => {
    const nlpEnabled =
        question.showAlternateWording ||
        question.showTopics ||
        question.showGenericProbe ||
        question.gibberish ||
        question.lazyAnswer;
    const cType = [
        {
            label: optionSettings.popupCardOptionMapping[
                question?.popupSettings?.popupCardTypes
            ],
            value: question?.popupSettings?.popupCardTypes
        }
    ];
    const tradeOffType = [
        {
            label: optionSettings.popupCardOptionMapping[
                question?.tradeoffSettings?.tradeoffTypes
            ],
            value: question?.tradeoffSettings?.tradeoffTypes
        }
    ];

    return (
        <SettingBoxLayout title="Question Settings" onClose={onClose}>
            <ShowSetting
                keyName="showQualityControl"
                setting={questionSettings}
            >
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Quality Control"
                            placement="bottom"
                            textSize="small"
                            icon={true}
                        >
                            Quality Control
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => {
                                setValue('gibberish', value);
                                setValue('lazyAnswer', value);
                                setValue('conversationalSettings', {
                                    ...question.conversationalSettings,
                                    qualityControlSettings: {
                                        ...question.conversationalSettings
                                            .qualityControlSettings,
                                        enableCheckDemeritGibberish: value,
                                        enableCheckDemeritUninformative: value,
                                        enableCheckDemeritAi: value
                                            ? question.conversationalSettings
                                                  .qualityControlSettings
                                                  .enableCheckDemeritAi
                                            : false,
                                        enableCheckDemeritDuplicate: value
                                            ? question.conversationalSettings
                                                  .qualityControlSettings
                                                  .enableCheckDemeritDuplicate
                                            : false
                                    }
                                });
                            }}
                            checked={
                                question.conversationalSettings
                                    ?.qualityControlSettings
                                    ?.enableCheckDemeritGibberish ||
                                question.conversationalSettings
                                    ?.qualityControlSettings
                                    ?.enableCheckDemeritUninformative ||
                                question.conversationalSettings
                                    ?.qualityControlSettings
                                    ?.enableCheckDemeritAi ||
                                question.conversationalSettings
                                    ?.qualityControlSettings
                                    ?.enableCheckDemeritDuplicate ||
                                question.lazyAnswer ||
                                question.gibberish
                            }
                            disabled={disabled || question.showValidation}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="showGenericProbe" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Smart Probe"
                            placement="bottom"
                            textSize="small"
                            icon={true}
                        >
                            SmartProbe
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => {
                                setValue('conversationalSettings', {
                                    ...question.conversationalSettings,
                                    smartProbeSettings: {
                                        ...question.conversationalSettings
                                            .smartProbeSettings,
                                        enableSmartProbe: value,
                                        enableTargets: !value
                                            ? value
                                            : question.conversationalSettings
                                                  .smartProbeSettings
                                                  .enableTargets,
                                        enableResearchContext: !value
                                            ? value
                                            : question.conversationalSettings
                                                  .smartProbeSettings
                                                  .enableResearchContext,
                                        enableTrainedModels: !value
                                            ? value
                                            : question.conversationalSettings
                                                  .smartProbeSettings
                                                  .enableTrainedModels
                                    }
                                });
                            }}
                            checked={
                                question.conversationalSettings
                                    ?.smartProbeSettings?.enableSmartProbe
                            }
                            disabled={disabled || question.showValidation}
                        />
                    </div>
                </div>
            </ShowSetting>

            <ShowSetting keyName="gibberish" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Prompt on Gibberish"
                            placement="bottom"
                            textSize="small"
                        >
                            Prompt on Gibberish
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => setValue('gibberish', value)}
                            checked={question.gibberish}
                            disabled={disabled || question.showValidation}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="lazyAnswer" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Prompt on Uninformative Answers"
                            placement="bottom"
                            textSize="small"
                        >
                            Prompt on Uninformative Answers
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => setValue('lazyAnswer', value)}
                            checked={question.lazyAnswer}
                            disabled={disabled || question.showValidation}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="allowUserEdit" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Allow User Corrections"
                            placement="bottom"
                            textSize="small"
                        >
                            Allow User Corrections
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('allowUserEdit', value)
                            }
                            checked={question.allowUserEdit}
                            disabled={
                                disabled ||
                                disableQuestionSettings?.allowUserEdit
                            }
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="skipPractice" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Skip Practice Question"
                            placement="bottom"
                            textSize="small"
                        >
                            Skip Practice Question
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        {/* TODO: update use of isVisible to skipPractice after updating stored procedures */}
                        <Toggle
                            onChange={(value) => setValue('isVisible', value)}
                            checked={question.isVisible}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting
                keyName="showContinueButton"
                setting={questionSettings}
            >
                <div className="setting-group question">
                    <label className="setting-group--label">
                        Include continue button
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('showContinueButton', value)
                            }
                            checked={question.showContinueButton}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting
                keyName="showNotApplicableOption"
                setting={questionSettings}
            >
                <div className="setting-group question">
                    <label className="setting-group--label">
                        Show not applicable
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('showNotApplicableOption', value)
                            }
                            checked={question.showNotApplicableOption}
                            disabled={disabled}
                        />
                    </div>
                </div>
                {/* </div> */}
            </ShowSetting>
            <ShowSetting
                keyName="alernateQuestionWording"
                setting={questionSettings}
            >
                <div className="setting-group question">
                    <label className="setting-group--label">
                        Alternate Phrasing
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('showAlternateWording', value)
                            }
                            checked={question.showAlternateWording}
                            disabled={disabled || question.showValidation}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="topics" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        Topic Detection
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => setValue('showTopics', value)}
                            checked={question.showTopics}
                            disabled={disabled || question.showValidation}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="randomGroup" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Randomize"
                            placement="bottom"
                            textSize="small"
                        >
                            Randomize All Cards
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => setValue('randomGroup', value)}
                            checked={question.randomGroup}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="piping" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        Enable piping option
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => setValue('piping', value)}
                            checked={question.piping}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="showValidation" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Text Validation"
                            placement="bottom"
                            textSize="small"
                        >
                            Text Validation
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('showValidation', value)
                            }
                            checked={question.showValidation}
                            disabled={disabled || nlpEnabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="allowCopyPaste" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Allow Copy and Paste"
                            placement="bottom"
                            textSize="small"
                        >
                            Allow Copy &amp; Paste
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('allowCopyPaste', value)
                            }
                            checked={question.allowCopyPaste}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="boxVisibility" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Show Labels"
                            placement="bottom"
                            textSize="small"
                        >
                            Show Boxes
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => setValue('isVisible', value)}
                            checked={question.isVisible}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="tradeoffTypes" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        Trade Off Types
                    </label>
                    <div className="setting-group--form-element">
                        <Select
                            className="react-select"
                            classNamePrefix="react-select"
                            name="form-field-name"
                            placeholder="Card type"
                            value={tradeOffType ? tradeOffType : null}
                            onChange={(value) =>
                                setValue('tradeoffTypes', value.value)
                            }
                            options={optionSettings.popupCardType}
                            components={{
                                IndicatorsContainer
                            }}
                            isDisabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="popupCardType" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        Popup Card Types
                    </label>
                    <div className="setting-group--form-element">
                        <Select
                            className="react-select"
                            classNamePrefix="react-select"
                            name="form-field-name"
                            placeholder="Card type"
                            value={cType ? cType : null}
                            onChange={(value) =>
                                setValue('popupCardTypes', value.value)
                            }
                            options={optionSettings.popupCardType}
                            components={{
                                IndicatorsContainer
                            }}
                            isDisabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="showFollowUp" setting={questionSettings}>
                <div className="setting-group question">
                    <label className="setting-group--label">
                        Enable Probing
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('showGenericProbe', value)
                            }
                            checked={question.showGenericProbe}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="allowDecimal" setting={questionSettings}>
                <div className="setting-group--wrapper">
                    <div className="setting-group question full">
                        <label className="setting-group--label">
                            Allow Decimal Numbers
                        </label>
                        <div className="setting-group--form-element flex">
                            <Toggle
                                onChange={(value) => {
                                    setValue('openEndedSettings', {
                                        ...(question?.openEndedSettings || {}),
                                        allowDecimal: value
                                    });
                                }}
                                checked={
                                    question?.openEndedSettings?.allowDecimal
                                }
                                disabled={disabled}
                            />
                            {question.openEndedSettings?.allowDecimal ? (
                                <input
                                    className={classnames(
                                        'form-group__control numeric-input',
                                        {
                                            error: errorObject?.numDecimals
                                        }
                                    )}
                                    type="number"
                                    value={
                                        question.openEndedSettings?.numDecimals
                                    }
                                    onChange={(e) => {
                                        setValue('openEndedSettings', {
                                            ...(question?.openEndedSettings ||
                                                {}),
                                            numDecimals: e.target.value
                                        });
                                    }}
                                    autoFocus
                                    disabled={disabled}
                                />
                            ) : null}
                        </div>
                    </div>
                    {errorObject?.numDecimals ? (
                        <p className="setting-group--error-msg">
                            {errorObject?.numDecimals}
                        </p>
                    ) : null}
                </div>
            </ShowSetting>
            <ShowSetting
                keyName="useThousandSeparator"
                setting={questionSettings}
            >
                <div className="setting-group question">
                    <label className="setting-group--label">
                        1,000 separator
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) => {
                                setValue('openEndedSettings', {
                                    ...(question?.openEndedSettings || {}),
                                    thousandSeparator: value
                                });
                            }}
                            checked={
                                question.openEndedSettings?.thousandSeparator
                            }
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting keyName="showMin" setting={questionSettings}>
                <div className="setting-group--wrapper">
                    <div className="setting-group question full">
                        <label className="setting-group--label">
                            Allow Minimum number
                        </label>
                        <div className="setting-group--form-element flex">
                            <Toggle
                                onChange={(value) => {
                                    setValue('openEndedSettings', {
                                        ...(question?.openEndedSettings || {}),
                                        showMin: value
                                    });
                                }}
                                checked={question?.openEndedSettings?.showMin}
                                disabled={disabled}
                            />
                            {question.openEndedSettings?.showMin ? (
                                <input
                                    className={classnames(
                                        'form-group__control numeric-input',
                                        {
                                            error: errorObject?.min
                                        }
                                    )}
                                    type="number"
                                    value={question.min}
                                    onChange={(e) =>
                                        setValue('min', Number(e.target.value))
                                    }
                                    disabled={disabled}
                                    autoFocus
                                />
                            ) : null}
                        </div>
                    </div>
                    {errorObject?.min ? (
                        <p className="setting-group--error-msg">
                            {errorObject?.min}
                        </p>
                    ) : null}
                </div>
            </ShowSetting>
            <ShowSetting keyName="showMax" setting={questionSettings}>
                <div className="setting-group--wrapper">
                    <div className="setting-group question full">
                        <label className="setting-group--label">
                            Allow Maximum number
                        </label>
                        <div className="setting-group--form-element flex">
                            <Toggle
                                onChange={(value) => {
                                    setValue('openEndedSettings', {
                                        ...(question?.openEndedSettings || {}),
                                        showMax: value
                                    });
                                }}
                                checked={question?.openEndedSettings?.showMax}
                                disabled={disabled}
                            />
                            {question.openEndedSettings?.showMax ? (
                                <input
                                    className={classnames(
                                        'form-group__control numeric-input',
                                        {
                                            error: errorObject?.max
                                        }
                                    )}
                                    type="number"
                                    value={question.max}
                                    onChange={(e) =>
                                        setValue('max', Number(e.target.value))
                                    }
                                    disabled={disabled}
                                    autoFocus
                                />
                            ) : null}
                        </div>
                    </div>
                    {errorObject?.min ? (
                        <p className="setting-group--error-msg">
                            {errorObject?.min}
                        </p>
                    ) : null}
                </div>
            </ShowSetting>
            <ShowSetting
                keyName="showDisappearingMedia"
                setting={questionSettings}
            >
                <div className="setting-group--wrapper">
                    <div className="setting-group question full">
                        <label className="setting-group--label">
                            {`Hide image after timer (in ms)`}
                        </label>
                        <div className="setting-group--form-element flex">
                            <Toggle
                                onChange={(value) => {
                                    setValue('multimediaSettings', {
                                        ...(question?.multimediaSettings || {}),
                                        disappearingMedia: value
                                    });
                                }}
                                checked={
                                    question?.multimediaSettings
                                        ?.disappearingMedia
                                }
                                disabled={disabled}
                            />
                            {question.multimediaSettings?.disappearingMedia ? (
                                <input
                                    className={classnames(
                                        'form-group__control numeric-input',
                                        {
                                            error: errorObject?.disappearingOffsetMilliseconds
                                        }
                                    )}
                                    type="number"
                                    value={
                                        question.multimediaSettings
                                            ?.disappearingOffsetMilliseconds
                                    }
                                    onChange={(e) =>
                                        setValue('multimediaSettings', {
                                            ...(question?.multimediaSettings ||
                                                {}),
                                            disappearingOffsetMilliseconds:
                                                Number(e.target.value)
                                        })
                                    }
                                    disabled={disabled}
                                    autoFocus
                                />
                            ) : null}
                        </div>
                    </div>
                    {errorObject?.disappearingOffsetMilliseconds ? (
                        <p className="setting-group--error-msg">
                            {errorObject?.disappearingOffsetMilliseconds}
                        </p>
                    ) : null}
                </div>
            </ShowSetting>
            <ShowSetting
                keyName="systemTextsOverride"
                setting={questionSettings}
            >
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Override System Texts"
                            placement="bottom"
                            textSize="small"
                        >
                            Override System Texts
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('systemTextsOverride', {
                                    enabled: value
                                })
                            }
                            checked={question.systemTextsOverride?.enabled}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
            <ShowSetting
                keyName="allowSkipFirstStage"
                setting={questionSettings}
            >
                <div className="setting-group question">
                    <label className="setting-group--label">
                        <HelperTooltip
                            pathname="Skip First Stage"
                            placement="bottom"
                            textSize="small"
                        >
                            Skip First Stage
                        </HelperTooltip>
                    </label>
                    <div className="setting-group--form-element">
                        <Toggle
                            onChange={(value) =>
                                setValue('skipFirstStage', value)
                            }
                            checked={question.isVisible}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </ShowSetting>
        </SettingBoxLayout>
    );
};

export default QuestionSettings;
